import { useState } from 'react';
import { api } from '../lib/api.js';

// ─── Change Kiosk PIN Modal ─────────────────────────────────────────────────
// Self-serve PIN change. Staff pick any 4-digit PIN — using their existing
// Square clock-in PIN is encouraged so they only have one number to
// remember. Server rejects collisions with another staff member's PIN.
export function ChangePinModal({ currentUser, onClose }) {
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    if (!/^\d{4}$/.test(pin)) { setError('PIN must be exactly 4 digits.'); return; }
    if (pin !== confirmPin) { setError('PINs do not match.'); return; }
    setSubmitting(true);
    try {
      await api('POST', '/api/users/me/pin', { pin },
        { 'x-user-id': String(currentUser.id) });
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Could not change PIN.');
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl flex flex-col fade-in shadow-2xl"
           style={{ maxHeight: '85vh', height: 'auto' }}
           onClick={e => e.stopPropagation()}>
        <div className="bg-ink rounded-t-2xl p-5 flex items-center justify-between flex-shrink-0">
          <div>
            <p className="text-gold text-xs uppercase tracking-widest mb-0.5">Account</p>
            <h3 className="text-cream font-bold text-base">Change kiosk PIN</h3>
          </div>
          <button onClick={onClose} className="text-cream/50 hover:text-cream text-3xl leading-none ml-4">×</button>
        </div>
        {success ? (
          <div className="p-6 text-center" style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
            <div className="text-5xl mb-3">✅</div>
            <p className="font-bold text-lg text-ink mb-1">PIN updated</p>
            <p className="text-sm text-gray-500 mb-6">Use the new PIN at any kiosk.</p>
            <button onClick={onClose} className="w-full py-3 bg-crimson text-white font-semibold rounded-xl hover:bg-crimson-dark">Done</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-3" style={{ overflowY: 'auto', WebkitOverflowScrolling: 'touch', flex: 1, minHeight: 0 }}>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
              Pick any 4-digit PIN — using the same number you use to clock in/out at Square is fine. Each PIN must be unique across the team.
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">New PIN</label>
              <input type="password" inputMode="numeric" pattern="\d*" maxLength={4} required autoFocus
                value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none focus:border-crimson"
                style={{ letterSpacing: '0.3em', fontFamily: 'monospace', fontSize: 18 }} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Confirm new PIN</label>
              <input type="password" inputMode="numeric" pattern="\d*" maxLength={4} required
                value={confirmPin} onChange={e => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none focus:border-crimson"
                style={{ letterSpacing: '0.3em', fontFamily: 'monospace', fontSize: 18 }} />
            </div>
            {error && <div className="text-xs text-red-600 font-medium">{error}</div>}
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={onClose}
                className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={submitting || pin.length !== 4 || confirmPin.length !== 4}
                className="flex-1 py-3 rounded-xl bg-crimson text-white font-semibold text-sm hover:bg-crimson-dark disabled:opacity-60">
                {submitting ? 'Saving…' : 'Update PIN'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
